import useCheckoutState, { checkoutStates } from './useCheckoutState';

import useRedirectAfterPayment from './useRedirectAfterPayment';
import getCheckoutSlug from '../services/getCheckoutSlug';
import removeUpsellSession from '../lib/removeUpsellSession';
import { useInitialCheckout, useInitialPayment, useUpsellCheckout } from '../queries/upsells';
import storeUpsellSession from '../lib/storeUpsellSession';
import { useEffect, useState } from 'react';
import logException from '../services/logException';

export default function useCheckoutUpsell() {
  const { seller, setState, payment, query, isPreview, isTestingPayments } = useCheckoutState();

  // The design preview must never navigate away from the checkout it is previewing.
  // Test payments render in a preview too, but they're a real (test-mode) purchase and
  // have to run the funnel — otherwise the pay button spins forever on a paid charge.
  const isUninteractivePreview = isPreview && !isTestingPayments;
  const [isLoading, setIsLoading] = useState(true);
  const [shouldHandleAfterUpsellAction, setShouldHandleAfterUpsellAction] = useState(false);
  const [shouldHandleUpsellAction, setShouldHandleUpsellAction] = useState(false);
  const [upsellData, setUpsellData] = useState(null);

  const {
    redirectUpsellCheckout,
    redirectUpsellURL,
    redirectCheckout,
    redirectURL,
    redirectConfirmation,
  } = useRedirectAfterPayment();

  // we wait for the upsell checkout, initial checkout and initial payment to be fetched, if they exist, before handling the upsell action
  // isInitialLoading becomes false even when fetching fails
  // which we currently rely on to handle the upsell action / shouldHandleUpsellAction
  const { data: upsellCheckout, isInitialLoading: upsellCheckoutIsInitialLoading } =
    useUpsellCheckout();
  const { data: initialCheckout, isInitialLoading: initialCheckoutIsInitialLoading } =
    useInitialCheckout();
  const { data: initialPayment, isInitialLoading: initialPaymentIsInitialLoading } =
    useInitialPayment();

  // set to ready
  useEffect(() => {
    if (
      !upsellCheckoutIsInitialLoading &&
      !initialCheckoutIsInitialLoading &&
      !initialPaymentIsInitialLoading
    ) {
      setIsLoading(false);
    }
  }, [
    upsellCheckoutIsInitialLoading,
    initialCheckoutIsInitialLoading,
    initialPaymentIsInitialLoading,
  ]);

  function handleUpsellAction(upsellFunnelStep, payment, checkout) {
    setUpsellData({
      upsellFunnelStep,
      payment,
      checkout,
    });

    setShouldHandleUpsellAction(true);
  }

  /**
   * Handle upsell action
   * it will wait for the initial checkout and payment data to be loaded before handling the after upsell action
   */
  useEffect(() => {
    if (!shouldHandleUpsellAction) return;
    if (isUninteractivePreview) {
      setShouldHandleUpsellAction(false);
      return;
    }
    if (isLoading) return;

    const { upsellFunnelStep, payment, checkout } = upsellData;

    storeUpsellSession(payment, checkout, seller._id, undefined);

    try {
      switch (upsellFunnelStep.config.action) {
        case 'checkout': {
          if (!upsellCheckout) {
            throw new Error('Upsell checkout not found');
          }
          const upsellCheckoutSlug = getCheckoutSlug(upsellCheckout);
          redirectUpsellCheckout(payment, upsellCheckoutSlug);
          break;
        }
        case 'redirect': {
          redirectUpsellURL(payment, upsellFunnelStep.config.redirect);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      logException(error);
    }
  }, [isLoading, shouldHandleUpsellAction]);

  function handleAfterUpsellAction() {
    setShouldHandleAfterUpsellAction(true);
  }

  /**
   * Handle after upsell action
   * it will wait for the initial checkout and payment data to be loaded before handling the after upsell action
   */
  useEffect(() => {
    if (!shouldHandleAfterUpsellAction) return;
    if (isUninteractivePreview) {
      setShouldHandleAfterUpsellAction(false);
      return;
    }

    if (isLoading) return;

    removeUpsellSession(undefined);

    switch (initialCheckout?.afterPaymentAction) {
      case 'confirmation':
        redirectConfirmation(initialPayment?._id, getCheckoutSlug(initialCheckout));
        break;
      case 'checkout':
        redirectCheckout(
          initialPayment,
          getCheckoutSlug(initialCheckout.redirectCheckout),
          false,
          payment?._id,
        );
        break;
      case 'redirect': {
        const dynamicRedirectUrl =
          query.redirect_url && initialCheckout.allowDynamicRedirectUrl ? query.redirect_url : null;

        const redirectUrl = dynamicRedirectUrl || initialCheckout.redirectUrl;

        redirectURL(
          initialPayment,
          {
            url: redirectUrl,
            path: initialCheckout.redirectUrlPath,
            query: initialCheckout.redirectUrlQuery,
          },
          initialCheckout.redirectUrlInsideEmbed,
        );
        break;
      }
      default:
        break;
    }

    setState(checkoutStates.INITIAL);
    setShouldHandleAfterUpsellAction(false);
  }, [isLoading, shouldHandleAfterUpsellAction]);

  return {
    handleUpsellAction,
    handleAfterUpsellAction,
    declineUpsell: handleAfterUpsellAction,
  };
}
